import { ButtonBase, Tooltip } from '@mui/material';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { mixColor, type SerpMapStyle } from '../../../api/src/fakes/serp-appearance';
import { heatmapPalette } from '../../../api/src/fakes/serp-heatmap';
import { presentQueryTile } from '../../../api/src/fakes/serp-tile-presentation';
import {
  SERP_TILE_METRICS,
  SERP_TILE_METRIC_DEFINITIONS,
  type SerpTileMetric,
} from '../../../api/src/fakes/serp-tile-metrics';
import type { TreemapRect } from '../../../api/src/fakes/serp-treemap';
import { volumeLabel } from '../../../api/src/fakes/query-volume';
import type { SerpMapRow } from '../../../api/src/fakes/serp-map-summary';
import { Box, Typography } from './mui-system';

export function SerpQueryTile({
  row,
  rect,
  top,
  mapStyle,
  palette,
  metrics,
  mobile,
  onSelect,
}: {
  row: SerpMapRow;
  rect: TreemapRect;
  top: number;
  mapStyle: SerpMapStyle;
  palette: ReturnType<typeof heatmapPalette>;
  metrics: readonly SerpTileMetric[];
  mobile: boolean;
  onSelect(): void;
}) {
  const state = presentQueryTile(row.summary, top, mapStyle, palette);
  const selected = SERP_TILE_METRICS.filter((key) => metrics.includes(key));
  const showLabel = mobile || (rect.width >= 64 && rect.height >= 40);
  const full = !mobile && rect.width >= 280 && rect.height >= 128 + selected.length * 42;
  const columns = rect.width >= 240 ? 3 : 2;
  const compact =
    mobile || (rect.width >= 150 && rect.height >= 106 + Math.ceil(selected.length / columns) * 33);
  const details = full || compact;
  const showWeight = mobile || rect.height >= 80;
  const muted = mixColor(state.ink, state.fill, 0.7);
  const metricLabels = state.metrics
    ? selected.map((key) => {
        const { count, bestRank } = state.metrics![key];
        return `${SERP_TILE_METRIC_DEFINITIONS[key].label}: ${count} из ${top}${bestRank === null ? '' : `, лучшая #${bestRank}`}`;
      })
    : [];
  const accessible = `${row.query} · ${state.label} · ${volumeLabel(row.volume)}${metricLabels.length ? ` · ${metricLabels.join(' · ')}` : ''}`;
  const signal = state.metrics ? state.signalLabel : 'Нет полных данных';
  const risk = ['fake', 'conflict', 'competitor'].includes(state.tone);
  return (
    <Tooltip
      title={`${accessible}${row.volume?.refresh?.error ? ` · ${row.volume.refresh.error}` : ''}${row.volume?.weightUnavailableReason ? ` · ${row.volume.weightUnavailableReason}` : ''}`}
    >
      <ButtonBase
        aria-label={accessible}
        data-query-tile={row.queryId}
        data-map-style={mapStyle}
        data-tile-density={full ? 'full' : compact ? 'compact' : 'minimal'}
        onClick={onSelect}
        sx={{
          position: { xs: 'relative', md: 'absolute' },
          left: { md: rect.x },
          top: { md: rect.y },
          width: { md: `${rect.width}px` },
          height: { md: `${rect.height}px` },
          minWidth: 0,
          minHeight: { xs: 150 + (selected.length > 2 ? 32 : 0), md: 0 },
          overflow: 'hidden',
          color: state.ink,
          bgcolor: showLabel ? state.fill : state.accent,
          borderRadius: 0.5,
          p: 0,
          textAlign: 'left',
          display: 'block',
          border: 0,
          backgroundImage: state.hatch
            ? 'repeating-linear-gradient(135deg,transparent 0 5px,#8885 5px 7px)'
            : undefined,
          '&:focus-visible': { outline: `2px solid ${palette.text}`, outlineOffset: -2 },
        }}
      >
        {showLabel && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: state.accent,
            }}
          />
        )}
        <Box
          sx={{
            position: { xs: 'relative', md: 'absolute' },
            inset: 0,
            p: details ? 1.5 : 1,
            minHeight: '100%',
            display: showLabel ? 'flex' : 'none',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: full ? 16 : 12,
                lineHeight: 1.3,
                pr: 1.5,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflowWrap: 'anywhere',
              }}
            >
              {row.priority ? '★ ' : ''}
              {row.query}
            </Typography>
            {showWeight && (
              <Typography
                sx={{
                  fontSize: full ? 13 : 11,
                  lineHeight: 1.3,
                  mt: 0.5,
                  color: muted,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {volumeLabel(row.volume)}
              </Typography>
            )}
          </Box>
          {details && (
            <Box sx={{ mt: 'auto', pt: 2, minWidth: 0 }}>
              {state.metrics && selected.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: full ? '1fr' : 'repeat(auto-fit,minmax(56px,1fr))',
                    columnGap: 1.5,
                    rowGap: full ? 1.5 : 1,
                  }}
                >
                  {selected.map((key) => {
                    const definition = SERP_TILE_METRIC_DEFINITIONS[key];
                    const { count, bestRank } = state.metrics![key];
                    return (
                      <Box key={key} sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                            alignItems: 'baseline',
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: full ? 13 : 11,
                              lineHeight: 1.3,
                              whiteSpace: 'nowrap',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {full
                              ? `${definition.label}: ${count} из ${top}`
                              : `${definition.compactLabel} ${count}`}
                          </Typography>
                          {full && (
                            <Typography sx={{ fontSize: 11, color: muted, whiteSpace: 'nowrap' }}>
                              {bestRank === null ? `нет в топ-${top}` : `лучшая #${bestRank}`}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          role="meter"
                          aria-label={`${definition.label}: ${count} из ${top}`}
                          aria-valuemin={0}
                          aria-valuemax={top}
                          aria-valuenow={count}
                          sx={{
                            height: full ? 5 : 3,
                            bgcolor: state.track,
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${(count / top) * 100}%`,
                              borderRadius: 'inherit',
                              bgcolor: mixColor(palette[definition.tone], state.ink, 0.7),
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.5,
                  mt: state.metrics && selected.length ? 1.5 : 0,
                  pt: full && state.metrics && selected.length ? 1 : 0,
                  borderTop:
                    full && state.metrics && selected.length
                      ? `1px solid ${state.track}`
                      : undefined,
                  color: full ? state.ink : muted,
                }}
              >
                {full && risk && (
                  <WarningAmberRounded sx={{ fontSize: 14, mt: '1px', flexShrink: 0 }} />
                )}
                <Typography
                  sx={{
                    fontSize: 11,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {signal}
                  {!full && state.metrics && selected.length ? ` · топ-${top}` : ''}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </ButtonBase>
    </Tooltip>
  );
}
